// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// Self-contained "find similar" dialog (T-304). Drop it next to any SOP, grow, or
// strain with its source table + id; it fetches semantic neighbors on open and
// renders them. Kept dependency-light so it can be added to existing pages with
// minimal risk.

import { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { PrometheanMark } from "@/components/PrometheanMark";
import { useSimilar, SimilarSourceTable } from "@/hooks/prometheus-agent/useSimilar";
import { SimilarNeighborRow } from "@/components/prometheus-agent/SimilarNeighborRow";

interface SimilarItemsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceTable: SimilarSourceTable;
  sourceId: string;
  title?: string;
  description?: string;
  onSelect?: (sourceId: string) => void;
  selectHint?: string;
}

const DEFAULT_DESCRIPTION = "From this facility.";
const STRAIN_DESCRIPTION = "Click a strain for pairing, traits, and history.";

export function SimilarItemsDialog({
  open,
  onOpenChange,
  sourceTable,
  sourceId,
  title = "Similar items",
  description,
  onSelect,
  selectHint,
}: SimilarItemsDialogProps) {
  const { loading, available, neighbors, error, fetchSimilar } = useSimilar();
  const body =
    description ??
    (sourceTable === "strains" ? STRAIN_DESCRIPTION : DEFAULT_DESCRIPTION);

  useEffect(() => {
    if (open && sourceId) {
      fetchSimilar(sourceTable, sourceId);
    }
  }, [open, sourceId, sourceTable, fetchSimilar]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PrometheanMark className="h-4 w-4" title="" />
            {title}
          </DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Searching…
          </div>
        )}

        {!loading && error && (
          <p className="text-sm text-destructive py-4">{error}</p>
        )}

        {!loading && !error && available === false && (
          <p className="text-sm text-muted-foreground py-4">
            Nothing to compare yet.
          </p>
        )}

        {!loading && !error && available && neighbors.length === 0 && (
          <p className="text-sm text-muted-foreground py-4">No similar items found.</p>
        )}

        {!loading && !error && neighbors.length > 0 && (
          <ul className="space-y-2 max-h-96 overflow-auto">
            {neighbors.map((n) => (
              <SimilarNeighborRow
                key={n.source_id}
                neighbor={n}
                selectable={Boolean(onSelect)}
                onSelect={onSelect}
                hint={onSelect ? selectHint : undefined}
              />
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default SimilarItemsDialog;
